import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentFactoryResolver, ElementRef, Injector, Type, ViewChild, ViewContainerRef } from "@angular/core";
import { deepCompareEQ } from "@cf-shared/deep-helpers";
import { getPageClass, getPageName, isRootPageName } from 'app/app-page-helpers';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CsPage, CsCallablePage, CsPageComponentInterface, NavController, Page } from "providers/nav-controller";
import { hydrateLocalParams } from 'utils/nav-local-params';
import { AppService } from "services/app.service";
import { getTransitionSlideInOutMs, waitForAngularUpdate, waitForDrawnFrameOrTimeoutAsync } from "services/browser-utils";
import { LoggingService } from "services/logging.service";
import { navigationStartingEvent, PubSubService } from "services/pubsub.service";
import { describeError } from '@cf-shared/error-describe';
import { awaitPageHookBoundedAsync, capturePageHookOutcome, chooseEnterTimeoutReportName, isPageGuardRefusal, PAGE_ENTER_TIMEOUT_MS, PAGE_LEAVE_TIMEOUT_MS, PageHookOutcome, pageEntryStepTracker } from 'providers/page-lifecycle-guard';
import { LL } from 'i18n/i18n.service';
import { bootTimings } from 'app/boot-timings';
import { localOpTracker } from 'services/local-op-tracker';

/**
 * What kind of page transition this is: every decision the choreography needs that can be
 * worked out BEFORE the transition starts, and that nothing the transition itself does can
 * change. It is computed once at the top of setPageActive and handed to each stage.
 *
 * Everything in here is a pure function of the entering page, the previous page and the
 * direction, plus one live screen-width read. None of it reads the DOM classes or the page
 * stack that the transition goes on to mutate.
 *
 * That exclusion is the load-bearing part. Three decisions look like they belong here and
 * deliberately do NOT, because each is a SNAPSHOT of state a concurrent navigation can
 * change while setPageActive is waiting on a frame: whether the container was already in
 * modal mode, whether the entering page was sitting visible under a modal, and whether the
 * previous modal is being popped rather than merely covered. Each is read in setPageActive
 * at the exact moment its answer is needed, and moving one in here would answer a
 * different question than the code is asking.
 */
interface PageTransitionKind {
  /** the entering page's modal marker, or null when it is an ordinary page */
  readonly modalMarker: ModalPageMarker | null;
  /** whether the entering page is a modal */
  readonly isModal: boolean;
  /**
   * whether the previous page is a modal.
   *
   * There is deliberately no `prevModalMarker` beside this, even though the entering page's
   * marker IS carried: nothing outside describePageTransition ever needs the previous
   * page's marker itself, only the two answers derived from it here.
   */
  readonly prevWasModal: boolean;
  /** whether the app is currently laid out for a small screen */
  readonly isSmallScreen: boolean;
  /** whether the ENTERING modal is drawn as a centred card with a backdrop, rather than full-screen */
  readonly modalUsesDesktopBehavior: boolean;
  /** whether the PREVIOUS modal is drawn that way — the popping element's own layout */
  readonly prevModalUsesDesktopBehavior: boolean;
  /**
   * whether the modal-and-direction rule allows the leaving page's exit animation to start
   * before the entering page is prepared. It says nothing about whether there IS a leaving
   * page — the caller checks that, because it needs the page itself anyway.
   */
  readonly canStartLeaveEarly: boolean;
  /** whether the entering page is one of the root pages that show the bottom nav */
  readonly isRootPage: boolean;
}

/**
 * What the early-exit stage did, handed on to the stages that need to know.
 *
 * Before this existed the same three facts were three separate `let`s declared at the top
 * of setPageActive and read up to 250 lines lower, which is what made the stages of the
 * transition impossible to separate.
 */
interface EarlyLeaveState {
  /** whether the leaving page's exit animation was started before the entering page was prepared */
  readonly startedEarly: boolean;
  /** when that exit started, so the entering slide can be given the time that is LEFT */
  readonly startedAtMs: number;
  /**
   * the exit transition's end, listened for at the moment the exit STARTED.
   *
   * The cleanup stage must await this promise and must never make a fresh one: by the time
   * the cleanup runs the transition may already be over, and a listener attached only then
   * would sit out its full 10s fallback instead of resolving at once.
   */
  readonly transitionDone: Promise<void> | undefined;
}

/** what an EarlyLeaveState says when the exit was not started early */
const NO_EARLY_LEAVE: EarlyLeaveState = {
  startedEarly: false,
  startedAtMs: 0,
  transitionDone: undefined,
};

/**
 * The navigation operations that change the page stack, by the name they are reported under
 * when a refused one finds the stack changed underneath it.
 */
type NavigationName = 'setRoot' | 'setPages' | 'popToRoot' | 'popTo' | 'pop' | 'push' | 'replaceLast';

/**
 * The page stack as it was at one moment: the array itself, and a copy of what it held.
 *
 * Both halves are needed, and for different reasons. The array identity catches a second
 * navigation INSTALLING a different stack; the copy catches one CHANGING the array that is
 * already there, which keeps the same identity. A save that kept only the array would be the
 * defect df-43f7d1a3155f was: `splice` had already shortened it, so putting it back restored
 * nothing.
 */
interface StackSnapshot {
  /** the array `this.pages` pointed at */
  readonly stack: Page[];
  /** the pages it held, copied out, so a later in-place change cannot hide behind the same array */
  readonly pages: Page[];
}

/**
 * What one navigation operation did to the page stack, reported by the callback that did it so
 * that `changeStackAndActivateAsync` can activate the right page and undo the right change.
 */
interface StackChange {
  /**
   * the page to navigate to. `undefined` means the operation gave up before it had anything to
   * activate — `replaceLast` when the replacement page could not be built, `pop` when the stack
   * had nothing left under the page it took off — so the change is undone with nothing having
   * refused it.
   */
  readonly enteringPage: Page | undefined;
  /** the page being navigated away from, handed on to `setPageActive` */
  readonly leavingPage?: Page;
  /** which way the transition animates; `setPageActive` reads an absent value as 'push' */
  readonly direction?: 'push' | 'pop';
  /**
   * the pages this change took OUT of the stack. A navigation that goes through closes them; a
   * refused one puts them back, or closes them when the stack has moved on and nothing else
   * would.
   */
  readonly removedPages: Page[];
  /** the pages this change put INTO the stack */
  readonly addedPages: Page[];
  /**
   * the pages this change BUILT, which a refusal has to destroy so the page's `onDestroy`
   * callbacks run — including the one that answers whoever opened a callable page.
   *
   * Usually the very same pages as `addedPages`. The two differ only when a change built a page
   * and then gave up before putting it in the stack, which is `replaceLast`'s first bail-out.
   */
  readonly builtPages: Page[];
}

/**
 * Work out what kind of transition this is. A free function rather than a method, because
 * it needs nothing from the component — which is what makes it safe to call before any of
 * the choreography has run.
 */
function describePageTransition(
  newPage: Page,
  prevActivePage: Page | undefined,
  direction: 'push' | 'pop',
): PageTransitionKind {
  const componentRef = newPage.component;

  // whether each page is a modal, by checking whether it provides ModalPageMarker.
  // note: modal classes and CSS variables are applied in createPage() before DOM insertion
  const modalMarker = componentRef.injector.get(ModalPageMarker, null);
  const prevModalMarker = prevActivePage?.component.injector.get(ModalPageMarker, null) ?? null;
  const isModal = modalMarker !== null;
  const prevWasModal = prevModalMarker !== null;

  const isSmallScreen = hasClientSmallScreen();

  // determine if modals should use desktop-like behavior (centered, backdrop, animations)
  // on small screens, modals use full-screen page behavior unless fullScreenOnMobile is false
  //
  // MIRRORED RULE — `isFloatingCard` in cs-header.ts asks the same question, because the header
  // draws a close X on a modal shown as a floating card and a back arrow on one shown
  // full-screen. Change this line and that one together, or the glyph stops matching the
  // presentation. Find both with: grep -rn "fullScreenOnMobile === false" client/src
  const modalUsesDesktopBehavior = !isSmallScreen || modalMarker?.config?.fullScreenOnMobile === false;
  const prevModalUsesDesktopBehavior = !isSmallScreen || prevModalMarker?.config?.fullScreenOnMobile === false;

  // the entering page is never a modal on the early-exit path (both directions exclude
  // it). For a PUSH the previous page's modal-ness is deliberately NOT checked — this
  // keeps the branch byte-equivalent to the original push-only behavior. For a POP the
  // previous page IS the one being popped, so a modal there must be excluded so the
  // modal-on-modal WAAPI close / backdrop branches run instead. design-docs/navigation.md
  // states the same asymmetric rule under "Navigation timing: early exit + immediate slide".
  const canStartLeaveEarly = !isModal
    && (direction === 'push' || (direction === 'pop' && !prevWasModal));

  return {
    modalMarker,
    isModal,
    prevWasModal,
    isSmallScreen,
    modalUsesDesktopBehavior,
    prevModalUsesDesktopBehavior,
    canStartLeaveEarly,
    // whether the target page is a root page, which is what decides the bottom nav.
    // isRootPageName owns the list of root page names — cs-nav used to keep its own copy.
    isRootPage: isRootPageName(getPageName(componentRef.componentType)),
  };
}

@Component({
  selector: 'cs-nav',
  templateUrl: 'cs-nav.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsNavComponent {

  pages: Page[] = [];
  private readonly backdropTransitionDuration = 200;

  // modal-on-modal close animation tuning. duration matches the open transition in
  // cs-nav.scss (0.2s for `.cs-page.modal-page` and `.fixed-size-mobile.active`); easing
  // is the natural reverse of the open's cubic-bezier(0.0, 0.0, 0.2, 1) decelerate curve.
  private static readonly MODAL_CLOSE_DURATION_MS = 200;
  private static readonly MODAL_CLOSE_EASING = 'cubic-bezier(0.4, 0.0, 1.0, 1.0)';

  /** Result from ionViewCanLeave when it returns { result: ... } instead of boolean */
  lastLeaveResult: any = undefined;

  @ViewChild('pagesContainer', { read: ViewContainerRef, static: true })
  pagesContainer: ViewContainerRef;

  @ViewChild('modalBackdrop', { static: true })
  modalBackdropRef: ElementRef<HTMLElement>;

  constructor(
    private elementRef: ElementRef,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private navCtrl: NavController,
    private appService: AppService,
    private cd: ChangeDetectorRef,
    private logging: LoggingService,
    private pubSub: PubSubService,
  ) {
    this.navCtrl.setNav(this);
    // Initialization moved to NavController.initializeNavigationSystem()

    // disable iOS swipe-back gesture by preventing touches near left edge
    this.disableIOSSwipeGesture();
  }

  private disableIOSSwipeGesture() {
    const navElement = this.elementRef.nativeElement as HTMLElement;
    const edgeThreshold = 15; // pixels from left edge

    // prevent touchstart near left edge (where swipe gesture starts)
    navElement.addEventListener('touchstart', (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch.clientX < edgeThreshold) {
        event.preventDefault();
      }
    }, { passive: false });
  }

  ngOnDestroy() {
    if (this.navCtrl.nav === this) {
      this.navCtrl.setNav(undefined);
    }
  }

  getElementRef(): ElementRef {
    return this.elementRef;
  }

  canGoBack(): boolean {
    return this.pages.length > 1;
  }

  handleBackdropClick() {
    // dismiss modal by going back
    void this.navCtrl.popAsync();
  }

  // wait for a CSS transition on an element to be over — no matter HOW it
  // ends. There are three endings, and all three must resolve this wait:
  //  - it finishes cleanly: `transitionend` fires (the normal, fast path);
  //  - it is interrupted by a concurrent class/style change (e.g. a second
  //    navigation retargets the transform mid-flight): `transitioncancel`
  //    fires INSTEAD of `transitionend`;
  //  - it dies silently: the element is removed from the DOM mid-transition,
  //    and NO transition event fires at all.
  // The wait used to handle only `transitionend`, so the other two endings
  // sat out the full 10s fallback. That stall is not cosmetic: setRoot()
  // destroys the pages it replaced only after setPageActive() returns, so a
  // stalled wait kept the old page in the DOM (hidden) for ~10 seconds.
  private async waitForTransition(element: HTMLElement, timeoutMs: number = 10000): Promise<void> {
    // wait for next frame to ensure transitions have started. CAPPED (#4616): a
    // browser that is not drawing never hands out a frame, and this wait runs
    // BEFORE the fallback timer below is armed, so uncapped it had no safety net
    // at all and hung the whole boot. See design-docs/boot-sequence.md, "#4616".
    await waitForDrawnFrameOrTimeoutAsync();

    // a transition counts as over when the element left the DOM, or when no
    // animation on it is running (or about to run) anymore. A finished
    // animation kept alive by fill:forwards has playState 'finished', so it
    // correctly counts as settled here.
    const transitionsSettled = () =>
      !element.isConnected ||
      element.getAnimations().every(a => a.playState !== 'running' && !a.pending);

    // if nothing is animating (or the element is already gone), return now
    if (transitionsSettled()) {
      return;
    }

    return new Promise((resolve) => {
      let completed = false;
      const finish = () => {
        if (!completed) {
          completed = true;
          clearTimeout(timeout);
          clearInterval(settleCheck);
          element.removeEventListener('transitionend', onTransitionEnd);
          element.removeEventListener('transitioncancel', onTransitionCancel);
          resolve();
        }
      };

      // timeout fallback in case no other signal ever arrives
      const timeout = setTimeout(finish, timeoutMs);

      // safety net for the silent ending (element removed from the DOM fires
      // no transition event) and anything else the listeners might miss:
      // periodically check the actual animation state. The healthy path still
      // resolves immediately via transitionend below, so this interval adds
      // no latency to normal navigation.
      const settleCheck = setInterval(() => {
        if (transitionsSettled()) {
          finish();
        }
      }, 250);

      // only respond to transitions of these properties (the page/modal
      // choreography animates exactly these), and only on the element itself,
      // not bubbled events from children
      const relevantProperties = ['transform', 'scale', 'opacity'];

      // listen for transition end (the normal ending)
      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.target === element && relevantProperties.includes(event.propertyName)) {
          finish();
        }
      };

      // an interrupted transition fires transitioncancel INSTEAD of
      // transitionend. A cancel can mean a retarget: the same style update
      // that canceled this transition may have started a replacement one. So
      // do not finish on the cancel itself — check one frame later whether
      // anything is still animating; if a replacement transition is running,
      // keep waiting and let ITS end (or the settle check) finish the wait.
      const onTransitionCancel = (event: TransitionEvent) => {
        if (event.target === element && relevantProperties.includes(event.propertyName)) {
          requestAnimationFrame(() => {
            if (!completed && transitionsSettled()) {
              finish();
            }
          });
        }
      };

      element.addEventListener('transitionend', onTransitionEnd);
      element.addEventListener('transitioncancel', onTransitionCancel);
    });
  }

  // animate the modal backdrop in or out
  private animateBackdrop(show: boolean): void {
    const backdrop = this.modalBackdropRef?.nativeElement;
    if (!backdrop) return;

    if (show) {
      backdrop.animate(
        [
          { background: 'transparent', backdropFilter: 'blur(0px)', visibility: 'visible' },
          { background: 'var(--shadow-color)', backdropFilter: 'blur(1px)', visibility: 'visible' }
        ],
        { duration: this.backdropTransitionDuration, fill: 'forwards', easing: 'cubic-bezier(0.0, 0.0, 0.2, 1)' }
      );
    } else {
      backdrop.animate(
        [
          { background: 'var(--shadow-color)', backdropFilter: 'blur(1px)', visibility: 'visible' },
          { background: 'transparent', backdropFilter: 'blur(0px)', visibility: 'hidden' }
        ],
        { duration: this.backdropTransitionDuration, fill: 'forwards', easing: 'cubic-bezier(0.0, 0.0, 0.2, 1)' }
      );
    }
  }

  getActive(): Page {
    return this.pages[this.pages.length - 1];
  }

  getPrevious(page?: Page): Page {
    if (!page) {
      return this.pages[this.pages.length - 2];
    }
    let index = this.pages.indexOf(page);
    if (index > 0) {
      return this.pages[index - 1];
    }

    return undefined;
  }

  getRootPage(): Page {
    return this.pages[0];
  }

  /**
   * Destroy page components that a cancelled navigation created and never showed.
   *
   * `setPageActive` only ever answers "no" before it inserts anything into the DOM, so a
   * page it refused was constructed, had `ionViewCanEnter` run on it, and then nothing
   * else. Once the rollback puts the previous page list back, nothing in the stack points
   * at it either. Every navigation that COMPLETES destroys the components it drops; the
   * rollbacks used to just drop them.
   *
   * What that costs is the callbacks `createPage` registers with `componentRef.onDestroy`.
   * A page opened with `callAsync` answers its caller from one of those, so a refused
   * callable page left its caller waiting for the rest of the session. Destroying it
   * answers that caller with null, which is what a cancelled call has always meant. This
   * is deliberately not the same as the refused-POP case `deliverCallResultOnPageDestroy`
   * describes, where the page is still on screen and answering would be wrong — here the
   * page never appeared at all.
   *
   * The two skips below are the load-bearing part rather than defensive padding.
   * `ionViewCanEnter` is allowed to start a second navigation without waiting for it, so
   * the stack can have moved while we were awaiting (see `undoRefusedStackChange`). Destroying a component that
   * something else has since taken over would swap this leak for a crash on a live
   * navigation.
   */
  private destroyAbandonedPages(abandonedPages: Page[]): void {
    for (let abandonedPage of abandonedPages) {
      if (!abandonedPage) {
        continue;
      }
      // if a navigation that ran while we were awaiting has adopted this page, leave it alone
      if (this.pages.includes(abandonedPage)) {
        continue;
      }
      // if it reached the screen after all, it is not abandoned
      if (this.pagesContainer.indexOf(abandonedPage.component.hostView) !== -1) {
        continue;
      }
      abandonedPage.component.destroy();
    }
  }

  /**
   * Take a page that has LEFT the stack off the screen and destroy its component.
   *
   * This is the committed-navigation counterpart of `destroyAbandonedPages` above. That one
   * owns what happens to a page a REFUSED navigation created and never showed; this one owns
   * what happens to a page a navigation that went through has dropped. Between them, the two
   * cover every way a page stops being part of the stack.
   *
   * It exists as one method because every navigation that removes pages needs exactly this,
   * and until now all six of them — `setPages`, `popToRoot`, `pop`, `replaceLast`, `popTo`
   * and `setRoot` — wrote it out by hand. A rule about closing a page then had to be applied
   * six times with nothing failing if a copy was missed, which is what went wrong in
   * `749aa99a7` (df-3e06d8bf556b). Put such a rule here and every caller gets it.
   *
   * The index lookup can miss: a page is only in the container once it has actually been
   * inserted, and a navigation refused before insertion leaves one that never got there. The
   * component is destroyed either way — that is what releases the page's `onDestroy`
   * callbacks, including the one that answers whoever opened a callable page.
   */
  private destroyRemovedPage(page: Page): void {
    const indexToRemove = this.pagesContainer.indexOf(page.component.hostView);
    if (indexToRemove !== -1) {
      this.pagesContainer.remove(indexToRemove);
    }
    page.component.destroy();
  }

  /**
   * Change the page stack, activate the page the change navigates to, and undo the change if
   * that page refuses to be entered.
   *
   * Every navigation that changes the stack goes through here — `setRoot`, `setPages`,
   * `popToRoot`, `popTo`, `pop`, `push` and `replaceLast` — so the parts they all need are
   * written once: the SAVE taken before the change, the COMMIT that closes the pages the change
   * dropped, and the UNDO that runs when `setPageActive` answers no.
   *
   * It exists for the reason `destroyRemovedPage` above exists, which is the same reason arriving
   * a second time. That one lifted out the commit half after one rule had to be applied to four
   * sites by hand (`749aa99a7`, df-3e06d8bf556b). The undo half stayed behind in five copies —
   * three helpers and two bare inline undos — and nothing failed when a copy was missed: the
   * copies in `replaceLast` (df-8142a4713cd2) and in `popToRoot` / `popTo` (df-43f7d1a3155f) were
   * each found to be wrong and fixed on their own, and `setRoot`, `setPages`, `pop` and `push`
   * were still running the older, wrong rule when this wrapper replaced them (df-b2aa3a244e31).
   *
   * `checkCanLeave` deliberately stays in the callers. It asks the page being LEFT whether it may
   * be left, and that happens before the stack is touched at all, so it is not part of this
   * sequence — which is why the operations that run it and the operations that do not can share
   * one wrapper without a flag telling them apart.
   *
   * `applyChange` may await: `replaceLast` builds its replacement page in the middle of its own
   * change. What the undo compares against is therefore not a snapshot taken afterwards — that
   * would already include whatever a second navigation did during the await — but the stack this
   * change SAID it would leave, worked out from the pages it reports removing and adding.
   *
   * @returns true when the page the change navigates to was entered, false when the change was
   *   undone.
   */
  private async changeStackAndActivateAsync(navigation: NavigationName, applyChange: () => StackChange | Promise<StackChange>): Promise<boolean> {
    const before: StackSnapshot = { stack: this.pages, pages: [...this.pages] };
    const change = await applyChange();
    // the array the change left behind: a new one for setRoot and setPages, the one that was
    // already there for everything else
    const stackAfterChange = this.pages;

    if (change.enteringPage && await this.setPageActive(change.enteringPage, change.leavingPage, change.direction)) {
      for (let removedPage of change.removedPages) {
        this.destroyRemovedPage(removedPage);
      }
      return true;
    }

    this.undoRefusedStackChange(navigation, change, before, stackAfterChange);
    this.destroyAbandonedPages(change.builtPages);
    return false;
  }

  /**
   * Put the page stack back after a refused navigation — but only while nothing else has touched
   * it.
   *
   * A page's `ionViewCanEnter` is allowed to start a second navigation without waiting for it and
   * then refuse (`subscribe.ts` did, before it deferred its redirect). So by the time this runs,
   * the stack can already belong to that second navigation, and putting the old pages back would
   * overwrite its work: the screen would show its page while `this.pages` still described the
   * pages this operation started from. The second navigation also takes the refused page as the
   * page it is replacing, so it never closes the pages that are still on screen. See
   * design-docs/navigation.md, "A refused navigation and a navigation its guard starts"
   * (df-2077c7f7295e). That section states the rule this method applies, and why each half of it
   * is there; it is the copy to read, and the one to keep true.
   *
   * The rule, in short: the saved pages go back only while the stack is still the one the change
   * said it would leave — the same array, holding the same pages, compared one by one. Otherwise
   * the newer stack is left alone, any page this change added is taken back out of whatever list
   * now holds it, the pages the undo would have put back are closed if the newer stack does not
   * hold them, and the guard that caused it is reported.
   */
  private undoRefusedStackChange(navigation: NavigationName, change: StackChange, before: StackSnapshot, stackAfterChange: Page[]): void {
    const keptPages = before.pages.filter(page => !change.removedPages.includes(page));
    const expectedPages = [...keptPages, ...change.addedPages];
    const stackUntouched = this.pages === stackAfterChange
      && stackAfterChange.length === expectedPages.length
      && stackAfterChange.every((page, index) => page === expectedPages[index]);
    if (stackUntouched) {
      if (stackAfterChange === before.stack) {
        // the change mutated this array in place, so the saved pages go back ONTO it — something
        // else may still be holding the same reference, and assigning an already shortened copy
        // back is what restored nothing in df-43f7d1a3155f
        stackAfterChange.length = 0;
        stackAfterChange.push(...before.pages);
      } else {
        this.pages = before.stack;
      }
      return;
    }
    // a page this change pushed landed on the NEWER list; left there, destroyAbandonedPages reads
    // this operation's own push as adoption by somebody else and skips it (df-8142a4713cd2)
    for (let addedPage of change.addedPages) {
      const addedPageIndex = this.pages.indexOf(addedPage);
      if (addedPageIndex !== -1) {
        this.pages.splice(addedPageIndex, 1);
      }
    }
    this.closePagesOrphanedByRefusal(change.removedPages, navigation, change.enteringPage);
  }

  async insertBefore(beforePage: Page, pageToInsert: Type<CsPageComponentInterface<any>>, params?: any) {
    let pageInstance = await this.createPage(pageToInsert, params);
    if (!pageInstance || pageInstance.component.componentType !== pageToInsert) {
      // the page filter swapped in a different page class, so the one we built is never used
      this.destroyAbandonedPages([pageInstance]);
      return;
    }
    let beforePageIndex = this.pages.indexOf(beforePage);
    this.pages.splice(beforePageIndex, 0, pageInstance);
  }

  async setPages(pages: { page: Type<CsPageComponentInterface<any>>, params: any }[]): Promise<void> {
    let newPages: Page[] = [];
    for (let iPage = 0; iPage < pages.length; iPage++) {
      let newPage = await this.createPage(pages[iPage].page, pages[iPage].params);
      if (!newPage) {
        break;
      }
      newPages.push(newPage);
    }
    if (newPages.length === 0) {
      return;
    }
    await this.changeStackAndActivateAsync('setPages', () => {
      const currentActivePage = this.getActive();
      const previousPages = this.pages;
      // the list we report as built is a COPY, taken before the assignment below makes
      // `this.pages` the very same array: a navigation running while we await would append to
      // it, and we must not destroy a page we did not create
      const builtPages = [...newPages];
      this.pages = newPages;
      return {
        enteringPage: newPages[newPages.length - 1],
        leavingPage: currentActivePage,
        removedPages: previousPages,
        addedPages: builtPages,
        builtPages,
      };
    });
  }

  /**
   * The "a newer navigation replaced the stack while a refusal was being undone" branch of the
   * undo above: close every page the undo would have put back that the current stack does not
   * hold, and report the guard that caused it.
   */
  private closePagesOrphanedByRefusal(pagesToRestore: Page[], navigation: NavigationName, refusedPage: Page | undefined): void {
    const orphanedPages = pagesToRestore.filter(page => !this.pages.includes(page));
    const activePage = this.getActive();
    this.logging.trackSilentProblem('cs-nav.refused-navigation-stack-replaced', {
      navigation,
      // `replaceLast` can reach this without a refused page at all: its first bail-out runs
      // before setPageActive, so nothing has refused anything there
      refusedPage: refusedPage ? getPageName(refusedPage.component.componentType) : 'none',
      activePage: activePage ? getPageName(activePage.component.componentType) : 'none',
      closedPages: orphanedPages.map(page => getPageName(page.component.componentType)).join(','),
    });
    for (let orphanedPage of orphanedPages) {
      this.destroyRemovedPage(orphanedPage);
    }
  }

  /**
   * Check ionViewCanLeave on the active page BEFORE modifying the stack.
   * Returns false if navigation was blocked (page stays in stack).
   */
  private async checkCanLeave(page: Page): Promise<boolean> {
    this.lastLeaveResult = undefined;
    try {
      let canLeaveResult = await page.instance.ionViewCanLeave();
      if (canLeaveResult === false) return false;
      if (canLeaveResult && typeof canLeaveResult === 'object' && 'result' in canLeaveResult) {
        this.lastLeaveResult = (canLeaveResult as any).result;
      }
    } catch (e) {
      // an unexpected crash while a page decides whether it may be left. The navigation is
      // still cancelled by the `return false` below, deliberately: the user's edits are
      // still on the page and closing it over a save that just failed would throw them
      // away. Everything caught here is reported, unlike the catch in canEnter — why, and
      // why this was a silent console.log until now: df-a4ee2fffa197 in the commit that
      // added this line.
      this.logging.trackAbnormalException(e, LL.errors.userFacing.couldNotLeavePage());
      return false;
    }
    return true;
  }

  async popToRoot(): Promise<void> {
    // the page the user is standing on when this starts is the page the guard belongs to. It is
    // NOT necessarily the page we end up leaving — see the leaving-page read below
    let pageAskedToLeave = this.getActive();
    if (!await this.checkCanLeave(pageAskedToLeave)) return;

    // if a concurrent navigation emptied the stack while we asked, there is nothing to go to
    let rootPage = this.getRootPage();
    if (!rootPage) return;

    // a concurrent navigation may have popped its way to the root while we asked, in which case
    // there is nothing left to do and the page we would name as leaving is the page we are
    // entering — which would tell it, mid-entry, that it is going away
    if (this.getActive() === rootPage) return;

    await this.changeStackAndActivateAsync('popToRoot', () => {
      // read the page we are leaving HERE, after the leave check and before the cut, not from a
      // value taken before the await: a navigation running during that await can have removed and
      // destroyed the page that was active then, and the change would go on naming a destroyed
      // page as the one it is leaving (df-b9772ae3c01e)
      const leavingPage = this.getActive();
      return {
        enteringPage: rootPage,
        leavingPage,
        direction: 'pop',
        // splice shortens `this.pages` in place; the undo puts the removed pages back on that
        // same array
        removedPages: this.pages.splice(1),
        addedPages: [],
        builtPages: [],
      };
    });
  }

  async pop(onCanLeave?: () => void | Promise<void>): Promise<void> {
    // run ionViewCanLeave BEFORE removing from stack so that
    // handleBrowserNavigation's blocked-nav detection (pages.length === depthBefore) works
    if (!await this.checkCanLeave(this.pages[this.pages.length - 1])) return;

    // ionViewCanLeave passed — invoke callback before animation
    if (onCanLeave) {
      await onCanLeave();
    }

    // NOW remove from stack, then run the rest of the transition
    await this.changeStackAndActivateAsync('pop', () => {
      const removedPage = this.pages.pop();
      const removedPages = removedPage ? [removedPage] : [];
      // when that page was the last one in the stack there is nothing left to go back to, so the
      // change enters no page and is simply undone — the popped page goes straight back
      const activePage = this.getActive();
      return {
        enteringPage: activePage,
        leavingPage: removedPage,
        direction: 'pop',
        removedPages,
        addedPages: [],
        builtPages: [],
      };
    });
  }

  /**
   * Atomically replace the top page with a new page.
   * This avoids race conditions from separate pop + push operations.
   */
  async replaceLast<ParamsType>(page: Type<CsPage<ParamsType>>, params?: ParamsType): Promise<void> {
    await this.changeStackAndActivateAsync('replaceLast', async () => {
      // `replaceLast` on an empty stack pops nothing, and the report a refusal writes reads a
      // page's component type — so the change says it removed a page only when it did
      const oldTopPage = this.pages.pop();
      const removedPages = oldTopPage ? [oldTopPage] : [];
      // this await is why the change is built here rather than before the stack is touched: the
      // replacement page is created between the pop and the push, and another navigation can
      // run while it is being created
      const newPage = await this.createPage(page, params);
      if (!newPage || newPage.component.componentType !== page) {
        // the page could not be built, or the page filter swapped in a different page class and
        // the one we built is never used. Nothing was pushed, and nothing has refused anything
        // — setPageActive has not run — so the change enters no page and is simply undone
        return { enteringPage: undefined, removedPages, addedPages: [], builtPages: newPage ? [newPage] : [] };
      }
      this.pages.push(newPage);
      return { enteringPage: newPage, leavingPage: oldTopPage, direction: 'push', removedPages, addedPages: [newPage], builtPages: [newPage] };
    });
  }

  async popTo(page: Page): Promise<void> {
    // if the page we are asked to go back to is not in the stack, there is nothing to pop to
    if (this.pages.findIndex(p => p.id === page.id) === -1) return;

    // the page the user is standing on when this starts is the page the guard belongs to. It is
    // NOT necessarily the page we end up leaving — see the leaving-page read below
    let pageAskedToLeave = this.getActive();
    if (!await this.checkCanLeave(pageAskedToLeave)) return;

    // a concurrent navigation can change the stack while we ask — it may have been shortened
    // below the target, or replaced wholesale by a stack at least this long, in which case the
    // index found before the await now points at a DIFFERENT page. So find the page again
    // rather than trusting that index, and go nowhere if it has gone. popToRoot re-reads its
    // root after its own await for the same reason. (df-f9190305a3c2)
    let indexOfPage = this.pages.findIndex(p => p.id === page.id);
    let targetPage = this.pages[indexOfPage];
    if (!targetPage) return;

    // the target is already the active page — either it always was, or a concurrent navigation
    // popped down to it while we asked. There is nothing to cut, and naming it as the page we
    // are leaving would tell it, mid-entry, that it is going away
    if (targetPage === this.getActive()) return;

    await this.changeStackAndActivateAsync('popTo', () => {
      // read the page we are leaving HERE, after the leave check and before the cut, not from a
      // value taken before the await: a navigation running during that await can have removed and
      // destroyed the page that was active then, and the change would go on naming a destroyed
      // page as the one it is leaving (df-b9772ae3c01e)
      const leavingPage = this.getActive();
      return {
        enteringPage: targetPage,
        leavingPage,
        direction: 'pop',
        // splice shortens `this.pages` in place — see popToRoot
        removedPages: this.pages.splice(indexOfPage + 1),
        addedPages: [],
        builtPages: [],
      };
    });
  }

  /**
   * Open a page on top of the stack.
   *
   * @returns the page this call added and opened, or undefined when it opened none: the page
   *   could not be built, the page filter swapped in another class, it re-entered the page
   *   already on top, or the page refused to be entered. NavController writes the browser
   *   history for exactly this page. Reading the top of the stack afterwards instead is wrong,
   *   because another push may have landed on top while this one's transition ran (#6122).
   */
  async push(page: Type<CsPageComponentInterface<any>>, params?: any): Promise<Page | undefined> {
    let newPage = await this.createPage(page, params);
    if (!newPage || newPage.component.componentType !== page) {
      // if nothing was built there is nothing to destroy (destroyAbandonedPages skips a missing
      // page); if the page filter swapped in a different page class, the one we built is never used
      this.destroyAbandonedPages([newPage]);
      return undefined;
    }
    const activePage = this.getActive();
    if (newPage.component.componentType === activePage.component.componentType && deepCompareEQ(params, activePage.component.instance.navParams.data, { autoFocusElementId: true })) {
      newPage.component.destroy();
      activePage.component.instance.navParams.data = params;
      hydrateLocalParams(activePage.component.instance.navParams.data);
      void activePage.component.instance.ionViewDidReEnter();
      return undefined;
    }
    const opened = await this.changeStackAndActivateAsync('push', () => {
      this.pages.push(newPage);
      return {
        enteringPage: newPage,
        // read AFTER the push, so it names the page this one is going on top of
        leavingPage: this.getPrevious(),
        removedPages: [],
        addedPages: [newPage],
        builtPages: [newPage],
      };
    });
    return opened ? newPage : undefined;
  }

  async setRoot(page: Type<CsPageComponentInterface<any>>, params?: any): Promise<void> {
    let newPage = await this.createPage(page, params);
    if (!newPage) {
      return;
    }
    await this.changeStackAndActivateAsync('setRoot', () => {
      const currentActivePage = this.getActive();
      const previousPages = this.pages;
      this.pages = [newPage];
      return {
        enteringPage: newPage,
        leavingPage: currentActivePage,
        removedPages: previousPages,
        addedPages: [newPage],
        builtPages: [newPage],
      };
    });
  }

  private async canEnter(component: CsPageComponentInterface): Promise<boolean> {
    try {
      let result = await component.ionViewCanEnter();
      this.cd.markForCheck();
      if (result === false) {
        return false;
      }
    } catch (e) {
      // a deliberate refusal has already reported itself where it was raised; anything else
      // is an unexpected crash in a page's entry decision and is reported here. Either way
      // the navigation is still cancelled by the `return false` below. Why it has to be told
      // apart rather than reported wholesale: isPageGuardRefusal in
      // providers/page-lifecycle-guard (df-09902f47064a).
      if (!isPageGuardRefusal(e)) {
        this.logging.trackAbnormalException(e, LL.errors.userFacing.couldNotOpenPage());
      }
      return false;
    }

    return true;
  }

  private async setPageActive(newPage: Page, leavingPageAsHandedOver?: Page, direction: 'push' | 'pop' = 'push'): Promise<boolean> {
    // guard against concurrent navigation emptying the stack (race between checkCanLeave and pages.pop)
    if (!newPage) return false;

    let componentRef = newPage.component;

    if (!(await this.canEnter(componentRef.instance))) {
      return false;
    }

    // df-0a59a496956c: the leaving page is settled before the await above, and a page's
    // ionViewCanEnter may start a second navigation that removes and destroys it meanwhile.
    // A destroyed page is already off screen, so treat it as if there were no previous
    // page: no leave hooks, no exit animation, and the screen name goes straight to the
    // entering page. This is the leaving-side mirror of the ionViewDidEnter check below.
    const prevActivePage = this.dropDestroyedLeavingPage(newPage, leavingPageAsHandedOver);

    // answer "what kind of transition is this" ONCE, here, and hand it to every stage
    // below. Everything in it is settled before the transition starts and cannot change
    // while it runs; the three decisions that CAN change are read further down, each at
    // the moment its answer is needed. See PageTransitionKind.
    const kind = describePageTransition(newPage, prevActivePage, direction);

    if (prevActivePage) {
      prevActivePage.component.instance._navPrepareLeave();
      void prevActivePage.component.instance.ionViewWillLeave();
    }

    // notify subscribers that navigation is starting (before any DOM changes)
    // this allows floating UI elements (like cs-page-action-buttons) to start their exit animation immediately
    this.pubSub.publish(navigationStartingEvent, undefined);

    // mark that a page transition is in progress BEFORE inserting component
    // this allows floating UI elements (like cs-page-action-buttons) to stay hidden until transition completes
    const navElement = this.elementRef.nativeElement as HTMLElement;
    navElement.classList.add('page-transition-active');

    // Perceived responsiveness: for a plain page-to-page navigation — forward
    // (push) OR back (pop) — start the previous page's exit animation NOW,
    // within a frame of the tap, instead of after the entering page has been
    // created and rendered. The entering page's preparation (create, insert,
    // first render, and for a pop its pop-entering left-positioning) then runs
    // WHILE the old page animates out, so the user sees an immediate reaction.
    // That preparation is short (~1-2 frames — there is no data head start), so
    // the entering page starts sliding in almost together with the leaving page:
    // they move as a pair, and the entering slide runs at its natural full-ish
    // duration (applyEnteringSlideDuration works that out). A push slides the
    // leaving page left (.leaving); a pop slides it right (.leaving.popping).
    // Modal branches keep their original choreography — backdrop timing in
    // settleModalBackdropAsync, and the reverse-scale WAAPI close and
    // previous-visible stacking in animateLeavingPageAsync — so the early exit is
    // gated off around them; describePageTransition's canStartLeaveEarly carries
    // the exact (asymmetric) modal rule. This runs AFTER both navigation gates
    // (ionViewCanLeave in NavController, canEnter above), so the navigation is
    // already committed.
    const earlyLeave = prevActivePage && kind.canStartLeaveEarly
      ? this.startLeavingPageExitEarly(prevActivePage, direction)
      : NO_EARLY_LEAVE;

    const { viewWillEnterOutcome } = await this.insertAndRenderEnteringPageAsync(componentRef);
    let componentElement = componentRef.location.nativeElement as HTMLElement;

    await this.settleModalBackdropAsync(navElement, prevActivePage, kind);

    // whether the entering page was sitting visible UNDER a modal. One of the three
    // snapshots PageTransitionKind deliberately does not carry: it must be read before the
    // cleanup on the next few lines strips that same class off.
    const wasUnderModal = componentElement.classList.contains('previous-visible');

    // clean up inline styles that may have been set
    componentElement.style.removeProperty('visibility');
    componentElement.style.removeProperty('pointer-events');
    componentElement.removeAttribute('e2e-hidden');
    componentElement.classList.remove('previous-visible');

    // if popping (going back), position page on left before animating
    // skip this for pages that were visible under a modal - they're already in position
    if (direction === 'pop' && !wasUnderModal && !kind.isModal) {
      await this.positionEnteringPageForPopAsync(componentElement);
    }

    // mark the nav element when the target page is NOT a root page, so the bottom nav
    // animates away with the transition
    if (!kind.isRootPage && kind.isSmallScreen && !(kind.isModal && kind.modalUsesDesktopBehavior)) {
      navElement.classList.add('animating-to-non-root');
    }

    // for auto-height modals, wait for content to render before adding .active
    if (kind.isModal && kind.modalMarker?.config?.height === 'auto') {
      await waitForAngularUpdate(this.injector);
    }

    // For any early-exit navigation (plain non-modal push OR pop), start the
    // slide NOW — do not render the entering page's content before adding
    // `.active`. The gate is `!earlyLeave.startedEarly`, which is true for both
    // of those, so this skip covers the pop path too.
    //
    // The entering page's cheap loading shell was already painted by the first
    // `waitForAngularUpdate` above (the one that runs ngOnInit): loading flags
    // and day labels are set in ngOnInit, so the page has a rendered shell to
    // slide on. A SECOND render here would, on a heavy page, do its expensive
    // layout before the slide starts. Measured on-device (#4242): Training's
    // exercise `cs-card-list` lays itself out in exactly this second render
    // when the workout data is already cached — ~110 ms of main-thread work
    // that (a) delayed `.active` (the entering page visibly lagged the tap) and
    // (b) via the `stagger` below, compressed the slide so it rushed in. Home,
    // which renders cheaply, never hit it. Skipping the second render lets the
    // slide start on the shell at ~1-2 frames and the heavy content render happen
    // DURING the slide instead — kept smooth by the `will-change` compositor
    // promotion on `.page-transition-active`. Content filling in mid-slide is the
    // intended behavior (see #4242 and the exercise-list fade removal).
    // `viewWillEnterOutcome` is still awaited after the transition for error
    // reporting.
    //
    // The remaining navigations keep the pre-`.active` render — the ones whose
    // early exit did not happen: a modal-involved navigation (an auto-height
    // modal must settle its content height before it scales in, which the wait
    // just before this one does; a popped page uncovered behind a modal is
    // already built), and a navigation with no previous page (nothing to overlap
    // the render with), which also renders here.
    if (!earlyLeave.startedEarly) {
      await waitForAngularUpdate(this.injector);
    }

    this.applyEnteringSlideDuration(componentElement, earlyLeave);

    // add .active class - CSS transitions handle the animation
    componentElement.classList.add('active')

    // animate previous page transition
    if (prevActivePage) {
      await this.animateLeavingPageAsync(prevActivePage, direction, kind, earlyLeave);
    }

    // trigger DOM update to start the CSS transition
    await waitForAngularUpdate(this.injector);
    // wait for page transition animation to complete
    await this.waitForTransition(componentElement);
    // remove animating classes now that transition is complete
    componentElement.style.removeProperty('transition-duration');
    navElement.classList.remove('animating-to-non-root');
    navElement.classList.remove('page-transition-active');

    await this.awaitAndReportPageEntryAsync(viewWillEnterOutcome, componentRef, componentElement);

    // df-53c1d46615c2: a page a later navigation already destroyed gets no after-slide
    // hook. #4413's reason for carrying on past a stalled page is a partly-loaded page
    // someone can use, and a destroyed page has nobody looking at it (see the report-name
    // comment in awaitAndReportPageEntryAsync). Running the hook there leaked listeners
    // attached after the page's own teardown, opened a checkout for a dead page, and let a
    // page pop the one that replaced it. A page still on screen gets the hook whether its
    // entry step finished, threw or timed out — that is the #4413 behaviour, kept.
    if (!componentRef.hostView.destroyed) {
      void componentRef.instance.ionViewDidEnter();
    }
    let newPageScreenName = componentElement.tagName.toLowerCase();
    if (prevActivePage) {
      await this.awaitAndReportPageLeaveAsync(prevActivePage, newPageScreenName);
    } else {
      this.appService.changeScreenName(newPageScreenName);
    }
    this.cd.markForCheck();

    return true;
  }

  /**
   * Answer the page `setPageActive` should treat as the one being left: the page it was handed,
   * unless a navigation that ran while the entering page decided whether to open has already
   * destroyed it. That case is reported, because it means some page's `ionViewCanEnter` started
   * a navigation without waiting for it (df-0a59a496956c; see design-docs/navigation.md).
   */
  private dropDestroyedLeavingPage(newPage: Page, leavingPage: Page | undefined): Page | undefined {
    if (!leavingPage || !leavingPage.component.hostView.destroyed) {
      return leavingPage;
    }
    this.logging.trackSilentProblem('cs-nav.leaving-page-destroyed-before-leave', {
      enteringPage: getPageName(newPage.component.componentType),
      leavingPage: getPageName(leavingPage.component.componentType),
    });
    return undefined;
  }

  /**
   * Start the leaving page's exit animation, before the entering page has been created or
   * rendered, so the user sees a reaction within a frame of the tap.
   *
   * Only reached when describePageTransition said the modal-and-direction rule allows it.
   */
  private startLeavingPageExitEarly(prevActivePage: Page, direction: 'push' | 'pop'): EarlyLeaveState {
    const prevActiveElement = prevActivePage.component.location.nativeElement as HTMLElement;
    const startedAtMs = performance.now();
    prevActiveElement.classList.add('leaving');
    // a pop slides the leaving page off to the RIGHT (.leaving.popping); a push
    // slides it left (.leaving alone)
    if (direction === 'pop') {
      prevActiveElement.classList.add('popping');
    }
    prevActiveElement.classList.remove('active');
    prevActiveElement.classList.remove('previous-visible');
    prevActiveElement.setAttribute('e2e-hidden', '');
    return {
      startedEarly: true,
      startedAtMs,
      // start listening for the exit transition end immediately — by the time
      // the cleanup in animateLeavingPageAsync runs, the transition may already be
      // over, and a listener attached only then would wait for its 10s timeout
      transitionDone: this.waitForTransition(prevActiveElement),
    };
  }

  /**
   * Put the entering page in the DOM, let it render its cheap shell once, and start its own
   * entry work. Answers with that entry work's outcome, which the caller waits on only after
   * the transition is over.
   *
   * The outcome comes back INSIDE an object, and that is load-bearing rather than tidy. An
   * async function that returned the promise directly would have it flattened by the
   * caller's `await`, so the caller would wait right here for the page's entry work — which
   * is the freeze #4413 removed. Wrapping it in an object makes `await` hand the promise
   * back unwaited, as an unstarted thing to be waited on later.
   */
  private async insertAndRenderEnteringPageAsync(
    componentRef: Page['component'],
  ): Promise<{ viewWillEnterOutcome: Promise<PageHookOutcome> }> {
    const indexOfComponent = this.pagesContainer.indexOf(componentRef.hostView);
    // For some reason if there is nothing in pagesContainer index of component is 0 and not -1.
    if (this.pagesContainer.length === 0 || indexOfComponent === -1) {
      this.pagesContainer.insert(componentRef.hostView);
    }

    // Wait for one frame for ngOnInit to be called.
    await waitForAngularUpdate(this.injector);

    componentRef.instance._navPrepareEnter();
    // #5686: redraw the page now that it is back. While it was covered its own guards may have
    // skipped redraws, and an OnPush page is never redrawn otherwise. This must be the page's OWN
    // view: the injector sits on its host node, so this is the same object the page gets as `cd`.
    // componentRef.hostView / componentRef.changeDetectorRef are the outer wrapper view and would
    // do nothing. See design-docs/navigation.md, "Read `viewDestroyed` for what it is".
    componentRef.injector.get(ChangeDetectorRef).markForCheck();
    // #4988: a new entry starts here, so the timeout report lists only THIS entry's
    // finished steps, not those of an earlier visit to the same page
    pageEntryStepTracker.beginEntry((componentRef.location.nativeElement as HTMLElement).tagName.toLowerCase());
    // capture the outcome HERE, where the promise is made, rather than at the wait far
    // later: the browser reports a promise nobody is listening to as an unhandled
    // rejection at the end of the current chunk of work, long before the transition
    // finishes, so a page that failed early was reported by the browser instead of by
    // our own trackAbnormalException. The waiting still happens after the transition.
    return {
      viewWillEnterOutcome: capturePageHookOutcome(componentRef.instance.ionViewWillEnter()),
    };
  }

  /**
   * Settle the modal backdrop and the container's `modal-mode` class for this transition,
   * and give a modal being popped its exit animation.
   */
  private async settleModalBackdropAsync(
    navElement: HTMLElement,
    prevActivePage: Page | undefined,
    kind: PageTransitionKind,
  ): Promise<void> {
    // whether the container was ALREADY showing a modal. A snapshot, read here rather
    // than in describePageTransition, because this method adds or removes that same
    // class — and a concurrent navigation may have changed it while the entering page's
    // frame wait was outstanding.
    const wasInModalMode = navElement.classList.contains('modal-mode');

    if (kind.isModal) {
      navElement.classList.add('modal-mode');
      // show backdrop if this modal uses desktop behavior and backdrop isn't already visible
      // backdrop is visible if: we were in modal mode AND the previous modal used desktop behavior
      const backdropWasVisible = wasInModalMode && kind.prevModalUsesDesktopBehavior;
      if (kind.modalUsesDesktopBehavior && !backdropWasVisible) {
        // on small screens with fixed-size modal, add class so backdrop CSS applies
        if (kind.isSmallScreen && kind.modalMarker?.config?.fullScreenOnMobile === false) {
          this.modalBackdropRef?.nativeElement.classList.add('fixed-size-modal-active');
        }
        this.animateBackdrop(true);
      }
      // hide backdrop if transitioning from desktop-behavior modal to non-desktop-behavior modal
      // (e.g., popping a fixed-size modal that was on top of a full-screen modal)
      if (backdropWasVisible && !kind.modalUsesDesktopBehavior) {
        this.animateBackdrop(false);
        this.modalBackdropRef?.nativeElement.classList.remove('fixed-size-modal-active');
      }
      return;
    }

    // check if modal is being popped (removed) vs just covered by a push. Another
    // snapshot: it reads the live page stack, which a concurrent navigation can change.
    const isPoppingModal = kind.prevWasModal && !this.pages.some(p => p === prevActivePage);

    // backdrop should hide when active page is no longer a modal (desktop behavior only)
    // start this first so it animates in parallel with modal close
    if (wasInModalMode && kind.prevModalUsesDesktopBehavior) {
      this.animateBackdrop(false);
      // remove fixed-size-modal-active class from backdrop
      this.modalBackdropRef?.nativeElement.classList.remove('fixed-size-modal-active');
    }

    // if popping a modal with desktop behavior, remove .active and let CSS transition handle exit animation
    if (isPoppingModal && kind.prevModalUsesDesktopBehavior && prevActivePage) {
      const prevElement = prevActivePage.component.location.nativeElement as HTMLElement;
      prevElement.classList.remove('active');
      // wait for CSS transition (200ms) before continuing
      await this.waitForTransition(prevElement, 300);
    }

    navElement.classList.remove('modal-mode');
  }

  /**
   * Put a page entering on a back-navigation at its starting position on the left, with no
   * transition, and paint it there before the CSS transition takes over.
   */
  private async positionEnteringPageForPopAsync(componentElement: HTMLElement): Promise<void> {
    // add class to position on left with no transition
    componentElement.classList.add('pop-entering');
    // wait for browser to paint the position before removing class
    // NOTE: using a frame wait instead of synchronous reflow because synchronous
    // approaches (offsetWidth, getComputedStyle) can be optimized away in production.
    // CAPPED (#4616) for the same reason as the wait in waitForTransition: the class
    // removed on the next line MUST come off, so this wait may never hang.
    await waitForDrawnFrameOrTimeoutAsync();
    // remove the class so CSS transitions take over
    componentElement.classList.remove('pop-entering');
  }

  /**
   * Give the entering page the time that is LEFT of the standard slide, so it lands together
   * with the leaving page. Does nothing when the exit was not started early, because then
   * there is nothing to catch up with.
   */
  private applyEnteringSlideDuration(componentElement: HTMLElement, earlyLeave: EarlyLeaveState): void {
    if (!earlyLeave.startedEarly) {
      return;
    }
    // pin the whole ceremony to the standard slide duration: the exit has been
    // running for `stagger` ms already (now just the page's prep time, ~1-2
    // frames, since there is no head start), so the entering page gets exactly
    // the REMAINING time as its transition duration — both pages finish
    // together at getTransitionSlideInOutMs() after the tap, which is the total
    // that --transition-slide-in-out-ms consumers assume for a page transition.
    // With the tiny stagger the entering slide is now essentially the full
    // duration, so it runs at its natural speed. Derived at runtime from the
    // same CSS token, so there is no second duration constant to keep in sync.
    // Clamped to at least half the full duration so a main-thread hiccup can
    // never degrade the slide into a near-snap (that rare case overruns the
    // total slightly instead). The inline style is removed after the
    // transition, so later transitions of this element (leaving, modal
    // re-activation) run at normal speed.
    const fullDurationMs = getTransitionSlideInOutMs();
    const staggerMs = performance.now() - earlyLeave.startedAtMs;
    const enterDurationMs = Math.max(fullDurationMs - staggerMs, fullDurationMs / 2);
    componentElement.style.transitionDuration = `${Math.round(enterDurationMs)}ms`;
  }

  /**
   * Animate the page being left behind, in whichever of the three ways this transition calls
   * for. See design-docs/navigation.md "Previous-page lifecycle during a transition" for the
   * three-branch breakdown and why the modal-on-modal pop needs WAAPI instead of the CSS
   * transition.
   *
   * On a POP this waits for the cross-fade cleanup, because the caller destroys the popped
   * page as soon as setPageActive returns and would otherwise cut the slide off. On a PUSH
   * the cleanup is deliberately NOT awaited: the page stays in the stack and may be shown
   * again later, and waiting would hold up the forward navigation for the whole slide.
   */
  private async animateLeavingPageAsync(
    prevActivePage: Page,
    direction: 'push' | 'pop',
    kind: PageTransitionKind,
    earlyLeave: EarlyLeaveState,
  ): Promise<void> {
    let prevActiveElement = prevActivePage.component.location.nativeElement as HTMLElement;

    // gate on prevModalUsesDesktopBehavior (the popping element's layout) — using the
    // NEW page's modalUsesDesktopBehavior would fire WAAPI on full-screen prevs that
    // aren't centered, jerking them off-screen.
    const isModalOnModalPop =
      direction === 'pop' && kind.isModal && kind.prevWasModal && kind.prevModalUsesDesktopBehavior;
    if (isModalOnModalPop) {
      // if popping a modal off a modal stack, run the open animation in reverse via
      // WAAPI and keep `.active` for the duration so `z-index: 100` stays applied.
      // animate the standalone `scale` property only — CSS3 `translate` and `transform`
      // are independent (desktop modals use `translate: -50% -50%` standalone), so
      // touching `transform` here would compose with the CSS translate and double-offset
      // the modal. standalone `scale` multiplies with `transform: scale(...)` so mobile
      // fixed-size modals (combined transform) still scale to 0 correctly.
      prevActiveElement.setAttribute('e2e-hidden', '');
      prevActiveElement.style.pointerEvents = 'none';
      const closeAnim = prevActiveElement.animate(
        [{ opacity: 1, scale: 1 }, { opacity: 0, scale: 0 }],
        {
          duration: CsNavComponent.MODAL_CLOSE_DURATION_MS,
          easing: CsNavComponent.MODAL_CLOSE_EASING,
          fill: 'forwards',
        },
      );
      try {
        await closeAnim.finished;
      } catch (err) {
        // if AbortError fired from element removal, the close is moot — proceed.
        // surface anything else (WAAPI bug, future error class) instead of swallowing.
        const isExpectedAbort = err instanceof DOMException && err.name === 'AbortError';
        if (!isExpectedAbort) {
          this.logging.trackSilentProblem('cs-nav.close-anim-throw', {
            message: describeError(err),
          });
        }
      }
      prevActiveElement.classList.remove('active');
      return;
    }

    if (kind.isModal && kind.modalUsesDesktopBehavior) {
      // push case: keep previous modal visible behind new one
      prevActiveElement.setAttribute('e2e-hidden', '');
      prevActiveElement.classList.remove('active');
      prevActiveElement.classList.add('previous-visible');
      return;
    }

    // cross-fade: animate out with .leaving class — unless the exit was
    // already started early (plain forward navigation, see
    // startLeavingPageExitEarly)
    if (!earlyLeave.startedEarly) {
      prevActiveElement.classList.add('leaving');
      if (direction === 'pop') {
        prevActiveElement.classList.add('popping');
      }
      prevActiveElement.classList.remove('active');
      prevActiveElement.classList.remove('previous-visible');
      prevActiveElement.setAttribute('e2e-hidden', '');
    }

    // wait for leaving animation to complete, then hide the page.
    // for an early-started exit, await the promise created at start time —
    // the transition may have already finished, and a listener attached
    // only now would sit out its full timeout
    const cleanupAfterAnimation = async () => {
      await waitForAngularUpdate(this.injector);
      await (earlyLeave.transitionDone ?? this.waitForTransition(prevActiveElement));
      prevActiveElement.classList.remove('leaving');
      prevActiveElement.classList.remove('popping');
      prevActiveElement.style.setProperty('visibility', 'hidden');
    };
    if (direction === 'pop') {
      await cleanupAfterAnimation();
    } else {
      void cleanupAfterAnimation();
    }
  }

  /**
   * Wait for the entering page's own entry work and report it if it failed or ran out of time.
   * Runs after the transition is complete, so nothing the user can see is held up by it.
   */
  private async awaitAndReportPageEntryAsync(
    viewWillEnterOutcome: Promise<PageHookOutcome>,
    componentRef: Page['component'],
    componentElement: HTMLElement,
  ): Promise<void> {
    // #4413: wait for the page's own entry work, but never indefinitely. This used to be a
    // bare `await viewWillEnterPromise` in a try/catch, which covered a page that THREW but
    // not one that simply never finished — and a page that never finished stopped everything
    // after it from running, including the line that clears the boot-time "restore in
    // progress" flag. The user was left on a dead spinner until the nav-restore watchdog gave
    // up 20 seconds later and threw them to Home. The transition itself is already complete
    // by the time this is called, so stepping over a stalled page is safe: they get a
    // partly-loaded page they can use instead of a frozen app.
    const enterOutcome = await awaitPageHookBoundedAsync(viewWillEnterOutcome, PAGE_ENTER_TIMEOUT_MS);
    if (enterOutcome.kind === 'failed') {
      this.logging.trackAbnormalException(enterOutcome.error);
    } else if (enterOutcome.kind === 'timedOut') {
      // name the page — this is the diagnostic NavRestoreStuck never carried, and it is how
      // we find out WHAT is hanging inside the page. Not an exception: nothing threw, and the
      // user is not blocked any more, so this is the "someone should look eventually" tier.
      //
      // df-7d1bc760ec40: ask FIRST whether this page still exists. A page a later navigation
      // destroyed mid-entry also lands here, and nothing above is true of it — nobody is
      // looking at it, so there is no partly-loaded page for anyone to use. The two get
      // separate report names so neither drowns the other; chooseEnterTimeoutReportName says
      // what the destroyed case means and why the name is the only place that distinction
      // can live.
      const timedOutPage = componentElement.tagName.toLowerCase();
      // #4537: naming the page took this as far as it goes — reading three weeks of this
      // report showed it is almost always the same page — so also say what that page was
      // still DOING. The three sources answer at three depths and none replaces another:
      // the entry step is the page's own await, the boot step is the app-wide phase still
      // open, and the local-op snapshot names a LOCAL database call that was issued and
      // never came back. An empty answer from any of them is itself information — see each
      // one's snapshot() for why absent is not zero. The field figures behind all of this
      // are in design-docs/boot-sequence.md, "What the #4413 report says, three weeks on".
      const entryStep = pageEntryStepTracker.snapshot();
      // the step tracker is shared by every page, and a page whose entry work is still
      // running keeps its step open while a LATER page enters and times out. So the oldest
      // open step is not necessarily THIS page's, and a tag saying it was would be a wrong
      // measurement rather than a missing one. The context below carries the step either
      // way, with the page that opened it; only the TAG is withheld when they disagree.
      const stepBelongsToThisPage = entryStep.page_entry_step_page === timedOutPage;
      this.logging.trackSilentProblem(chooseEnterTimeoutReportName(componentRef.hostView.destroyed), {
        page: timedOutPage,
        timeout_ms: enterOutcome.timeoutMs,
        ...entryStep,
        boot_step: bootTimings.currentPhase() ?? null,
        ...localOpTracker.snapshot(),
      }, {
        // as Sentry TAGS as well, because the context object above reaches Sentry only as
        // a breadcrumb, which cannot be grouped by or counted — reading this report meant
        // fetching events one at a time. Both values are low-cardinality (a page tag name,
        // and a step name from a fixed list), which is what a tag may carry.
        page: timedOutPage,
        ...(stepBelongsToThisPage && typeof entryStep.page_entry_step === 'string'
          ? { entry_step: entryStep.page_entry_step }
          : {}),
        // #4988: the part of that step still running, also from a fixed list
        ...(stepBelongsToThisPage && typeof entryStep.page_entry_inner_step === 'string'
          ? { entry_inner_step: entryStep.page_entry_inner_step }
          : {}),
      });
    }
  }

  /**
   * Wait for the leaving page's own finish hook, report it if it failed or ran out of time,
   * and tell the app which screen is now showing.
   */
  private async awaitAndReportPageLeaveAsync(prevActivePage: Page, newPageScreenName: string): Promise<void> {
    let prevActiveElement = prevActivePage.component.location.nativeElement as HTMLElement;
    let prevPageScreenName = prevActiveElement.tagName.toLowerCase();

    // df-0a59a496956c: a leaving page a later navigation destroyed DURING the transition gets
    // no after-leave hook, the same as the entering side's ionViewDidEnter check. The screen
    // name below is still updated — that is about the entering page, not this one.
    if (prevActivePage.component.hostView.destroyed) {
      if (newPageScreenName !== prevPageScreenName) {
        this.appService.changeScreenName(newPageScreenName);
      }
      return;
    }

    // wrap in try-catch to prevent errors from breaking navigation
    try {
      // df-26e08e4c2f75: bound this wait, the same way the ionViewWillEnter wait in
      // awaitAndReportPageEntryAsync is bounded. It used to be a bare `await`, which
      // covered a leaving page that THREW but did nothing for one that simply never
      // finished — and a page that never finished stopped everything after it from
      // running, including the line that clears the boot-time "restore in progress" flag.
      // The user was left on a dead spinner until the nav-restore watchdog gave up 20
      // seconds later and threw them to Home. The transition is already complete by this
      // point, so stepping over a stalled leave hook changes nothing the user can see.
      const leaveOutcome = await awaitPageHookBoundedAsync(
        capturePageHookOutcome(prevActivePage.component.instance.ionViewDidLeave()),
        PAGE_LEAVE_TIMEOUT_MS,
      );
      if (leaveOutcome.kind === 'failed') {
        this.logging.trackAbnormalException(leaveOutcome.error);
      } else if (leaveOutcome.kind === 'timedOut') {
        // name the page, for the same reason the entry side does: a report saying only
        // "some page stalled" tells us no more than NavRestoreStuck already did. Not an
        // exception — nothing threw, and the user is not blocked any more.
        this.logging.trackSilentProblem('PageDidLeaveTimeout', {
          page: prevActiveElement.tagName.toLowerCase(),
          timeout_ms: leaveOutcome.timeoutMs,
        }, {
          // #4537: the same page name as a Sentry TAG, for the same reason as the entry
          // side. No entry-step tag here — the step tracker follows a page's ENTRY
          // work, and this is the outgoing page's leave hook.
          page: prevActiveElement.tagName.toLowerCase(),
        });
      }
    } catch (error) {
      // still needed for a page whose hook is missing or throws synchronously, before
      // there is any promise for the guard to capture
      this.logging.trackAbnormalException(error);
    }
    if (newPageScreenName !== prevPageScreenName) {
      this.appService.changeScreenName(newPageScreenName);
    }
  }

  private async createPage(page: Type<CsPageComponentInterface<any>>, params: any): Promise<Page> {
    let requestedPageName = getPageName(page);
    let filteredPageName = await this.navCtrl.resolvePageFilterAsync(requestedPageName, params);
    if (filteredPageName !== requestedPageName) {
      console.log(`Nav: Page filter redirected from ${requestedPageName} to ${filteredPageName} during pageGuard.`);
      page = getPageClass(filteredPageName);
    }

    let componentFactory = this.resolver.resolveComponentFactory(page);
    const componentRef = componentFactory.create(this.injector);
    let element = componentRef.location.nativeElement as HTMLElement;
    element.classList.add('cs-page');

    // if modal page, add classes and CSS variables for initial state BEFORE DOM insertion
    const modalMarker = componentRef.injector.get(ModalPageMarker, null);
    if (modalMarker !== null) {
      element.classList.add('modal-page');
      // hide modal immediately via inline style to prevent flash (CSS may not apply fast enough)
      element.style.visibility = 'hidden';
      // apply all modal config before DOM insertion to prevent layout jumps
      if (modalMarker.config) {
        if (modalMarker.config.fullScreenOnMobile === false) {
          element.classList.add('fixed-size-mobile');
        }
        if (modalMarker.config.width) {
          element.style.setProperty('--modal-width', `${modalMarker.config.width}px`);
        }
        if (modalMarker.config.height) {
          if (modalMarker.config.height === 'auto') {
            // for auto-height, don't set --modal-height CSS var - CSS .auto-height class handles it
            element.classList.add('auto-height');
          } else {
            element.style.setProperty('--modal-height', `${modalMarker.config.height}px`);
          }
        }
      }
    }

    for (let key in params) {
      componentRef.instance.navParams.data[key] = params[key];
    }
    hydrateLocalParams(componentRef.instance.navParams.data);

    // if this is a callable page, set the navCtrl reference
    if (componentRef.instance instanceof CsCallablePage) {
      (componentRef.instance as any).navCtrl = this.navCtrl;
    }

    // answer whoever opened this page when its component goes away. This is
    // deliberately tied to DESTROY rather than to the pop: a page is destroyed
    // only after the pop transition finished, so by now the caller page has
    // re-entered and is live again. Answering any earlier lands in the window
    // where the caller still counts itself as gone and throws the answer away
    // (#4411). Destroy also covers every other way the page can end — browser
    // back, popTo, popToRoot, setRoot, replaceTop — so a page that closes by any
    // route answers its caller, not just one that closes by a clean pop.
    //
    // the flip side, stated plainly because it is easy to assume otherwise: a
    // page that is never destroyed never answers. A pop refused by
    // checkCanLeave, or one whose popstate never arrives so navigateBackAsync
    // gives up on its timeout, leaves the page on screen and the caller still
    // waiting. That is the correct state rather than a hang to paper over — the
    // page the user is looking at has not gone anywhere, and answering while it
    // is still up is precisely the bug above.
    //
    // the condition is the CALL ID, deliberately not `instanceof CsCallablePage`.
    // The page filter runs again a few lines above and can swap in a different
    // page class, while the params — and so the call id — carry over unchanged.
    // Gating on the class would leave that replacement page with no way to answer,
    // and its caller waiting for the rest of the session. The delete-and-resolve
    // it calls tolerates being asked about an unknown id, so a page carrying a
    // stale id costs nothing.
    const callId = componentRef.instance.navParams.data._callId;
    if (callId) {
      componentRef.onDestroy(() => this.navCtrl.deliverCallResultOnPageDestroy(callId));
    }

    let pageInstance: Page = {
      instance: componentRef.instance,
      component: componentRef,
      data: params,
      id: this.navCtrl.serializer.serialize(this.navCtrl.serializer.serializeComponent(componentRef.componentType, params)),
    };

    return pageInstance;
  }
}